import { Box, Fab, Portal } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { CompanyServiceSummary, getCompanyServices, getCompanyServicesForDownload, getCompanyServiceSummary } from "src/apis/company";
import { Service } from "src/apis/services";
import CompanySelect from "src/pages/components/CompanySelect";
import PageContainer from "src/pages/components/PageContainer";
import PaymentStatusFilter, { PaymentFilterValue } from "src/pages/components/PaymentStatusFilter";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import ServicesTable from "src/pages/components/ServicesTable";
import CompanyServicesTable from "src/pages/components/CompanyServicesTable";
import StartAndEndDateFilters from "src/pages/components/StartAndEndDateFilters";
import SummaryFab from "src/pages/components/SummaryFab";
import { getFirstDayOfMonth, getLastDayOfMonth } from "src/utils/date";
import { Id } from "src/utils/types";
import ServicesByCompanySummaryTable from "./components/ServicesByCompanySummaryTable";
import { getUserCompanyId, isUserAdministrator } from "src/utils/auth";
import { Download } from "@mui/icons-material";
import { downloadBlob } from "src/utils/blob";

const ServicesByCompany: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState<string>(getLastDayOfMonth());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<Id>('');
  const [services, setServices] = useState<Service[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [summary, setSummary] = useState<CompanyServiceSummary|null>();
  const [paymentFilter, setPaymentFilter] = useState<PaymentFilterValue>('all');

  const isAdmin = useMemo<boolean>(isUserAdministrator, []);

  const filterByPayment = useMemo(() => (
    (list: Service[], filter: PaymentFilterValue) => {
      if (filter === 'paid') return list.filter((service) => service.pago === true);
      if (filter === 'unpaid') return list.filter((service) => service.pago !== true);
      return list;
    }
  ), []);

  const fetchServices = async (): Promise<void> => {
    if (isAdmin && !companyId) return;

    setIsLoading(true);

    try {
      const id: Id = isAdmin ? companyId : getUserCompanyId();
      const resServices = await getCompanyServices(
        id,
        startDate,
        endDate);
      setAllServices(resServices);
      setServices(filterByPayment(resServices, paymentFilter));

      if (resServices.length > 0) {
        const resSummary = await getCompanyServiceSummary(
          id,
          startDate,
          endDate);
        setSummary(resSummary);
      }

      setError('')
    } catch (err: any) {
      setError(err?.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setServices(filterByPayment(allServices, paymentFilter));
  }, [allServices, paymentFilter, filterByPayment])

  const exportReports = async(): Promise<void> => {
    if (services.length === 0 || summary === null) return;

    const id: Id = isAdmin ? companyId : getUserCompanyId();
    const servicesBlob = await getCompanyServicesForDownload(id, startDate, endDate);
    const companyName = String(services[0].empresa).toLowerCase();

    downloadBlob(servicesBlob, `Atendimentos ${companyName} ${startDate} até ${endDate}.xlsx`);
  }

  return (
    <PageContainer title={!isAdmin ? "Atendimentos" : "Atendimentos por empresa"} error={error}>
      {Boolean(summary) && (
        <SummaryFab>
          <ServicesByCompanySummaryTable
            summary={summary!}
            startDate={startDate}
            endDate={endDate}
          />
        </SummaryFab>
      )}

      {(services.length > 0 || summary !== null) && (
        <Portal>
          <Fab
            color="primary"
            variant="extended"
            onClick={exportReports}
            sx={{
              margin: 0,
              marginBottom: 7,
              top: 'auto',
              right: 20,
              bottom: 20,
              left: 'auto',
              position: 'fixed',
              borderRadius: '5px',
            }}
          >
            <Download />
            Exportar Relatório
          </Fab>
        </Portal>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          justifyContent: "center",
          alignItems: "center",
          width: "100%"
        }}
      >
        {isAdmin && (
          <CompanySelect
            isFilter
            companyId={companyId}
            setCompanyId={setCompanyId}
            setError={setError}
          />
        )}

        <StartAndEndDateFilters
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        {isAdmin && (
          <PaymentStatusFilter
            value={paymentFilter}
            onChange={setPaymentFilter}
          />
        )}
        <FilterButtons
          isLoading={isLoading}
          isDisabled={!companyId && isAdmin}
          search={() => {
            fetchServices();
          }}
          resetState={() => {
            setCompanyId('');
            setServices([]);
            setAllServices([]);
            setSummary(null);
            setPaymentFilter('all');
          }}
        />
      </Box>

      {isAdmin ? (
        <ServicesTable
          hideConfidentialFields={!isAdmin}
          services={services}
          setError={setError}
        />
      ) : (
        <CompanyServicesTable
          hideConfidentialFields
          services={services}
        />
      )}
    </PageContainer>
  )
}

export default ServicesByCompany;
